import { useState } from 'react';
import { Modal } from './Modal';
import * as Sentry from '@sentry/remix';
import { userStore } from '~/lib/stores/user';

interface MigrationModalProps {
  isOpen: boolean;
  onContinue: () => Promise<boolean>;
  onComplete: () => void;
}

export const MigrationModal = ({ isOpen, onContinue, onComplete }: MigrationModalProps) => {
  const [migrating, setMigrating] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [failed, setFailed] = useState(false);

  console.log('[MigrationModal] isOpen:', isOpen);

  // This modal intentionally doesn't have a close handler to force users to proceed
  const handleContinue = async () => {
    setMigrating(true);

    try {
      const success = await onContinue();

      if (success) {
        setCompleted(true);
        setTimeout(() => {
          // Allow some time to see the success state before completing
          onComplete();
        }, 1500);
      } else {
        setFailed(true);

        // Even in case of failure, continue after a delay
        setTimeout(() => {
          onComplete();
        }, 3000);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error during migration',
        },
      });
      console.error('Migration failed:', error);
      setFailed(true);

      // Even in case of error, continue after a delay
      setTimeout(() => {
        onComplete();
      }, 3000);
    } finally {
      setMigrating(false);
    }
  };

  // Content based on the current state
  let content;

  if (completed) {
    content = (
      <div className="p-6 flex flex-col items-center text-center">
        <div className="i-ph:check-circle-duotone w-16 h-16 text-alpha-elements-icon-success mb-4"></div>
        <h2 className="text-2xl font-semibold text-white mb-3">Migration Completed</h2>
        <p className="text-[#e7e7e7]/70 text-sm mb-6">
          Your chat history has been successfully migrated to the latest version.
        </p>
      </div>
    );
  } else if (failed) {
    content = (
      <div className="p-6 flex flex-col items-center text-center">
        <div className="i-ph:warning-circle-bold w-16 h-16 text-alpha-elements-icon-error mb-4"></div>
        <h2 className="text-2xl font-semibold text-white mb-3">Migration Failed</h2>
        <p className="text-[#e7e7e7]/70 text-sm mb-6">
          There was a problem migrating your chat history. Please Contact Support.
        </p>
      </div>
    );
  } else if (migrating) {
    content = (
      <div className="p-6 flex flex-col items-center text-center">
        <div className="i-ph:circle-notch w-16 h-16 animate-spin text-alpha-primary-start mb-4"></div>
        <h2 className="text-2xl font-semibold text-white mb-3">Migrating Data</h2>
        <p className="text-[#e7e7e7]/70 text-sm mb-6">
          Please wait while we migrate your chat history to the latest version. This may take a moment.
        </p>
      </div>
    );
  } else {
    content = (
      <div className="p-6">
        <h2 className="text-2xl font-semibold text-white mb-3">Update Required</h2>
        <p className="text-[#e7e7e7]/70 text-sm mb-6">
          The chat interface has been updated to a newer version. We need to migrate your existing conversations to
          continue. This ensures all your previous chats are compatible with the latest features.
        </p>
        <button
          onClick={handleContinue}
          className="w-full px-6 py-3 bg-gradient-to-b from-alpha-primary-start to-alpha-primary-end rounded-lg text-white text-sm font-medium hover:brightness-94 transition-all"
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={() => {}}>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-51 w-full px-4 max-w-[480px]">
        <div className="bg-alpha-background-secondary rounded-xl border border-white/10 relative w-full">{content}</div>
      </div>
    </Modal>
  );
};
